import React, { useRef, useState } from "react";
import {
  UploadCloud,
  Trash2,
  Star,
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2,
  Image as ImageIcon,
} from "lucide-react";
import { cn } from "../lib/cn";

export interface MultiImageUploadProps {
  value: string[];
  onChange: (urls: string[]) => void;
  maxPhotos?: number;
  label?: string;
  description?: string;
  className?: string;
  disabled?: boolean;
}

export function compressAndReadImage(
  file: File,
  maxDimension = 1200,
  quality = 0.82
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (
      file.type === "image/svg+xml" ||
      file.type === "application/pdf" ||
      file.type.includes("pdf") ||
      file.name.toLowerCase().endsWith(".pdf")
    ) {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error(`Failed to read file ${file.name}`));
      reader.readAsDataURL(file);
      return;
    }

    // High-clarity 1200px - 1400px resolution for sharp text and full-screen expansion
    const effectiveMaxDim = Math.min(Math.max(maxDimension, 1000), 1400);
    const effectiveQuality = Math.min(Math.max(quality, 0.75), 0.88);
    // Allow up to ~140 KB base64 payload (gives razor-sharp 1080p WebP detail without bloating DB)
    const MAX_BASE64_LENGTH = 140_000;

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        if (width > effectiveMaxDim || height > effectiveMaxDim) {
          if (width > height) {
            height = Math.max(1, Math.round((height * effectiveMaxDim) / width));
            width = effectiveMaxDim;
          } else {
            width = Math.max(1, Math.round((width * effectiveMaxDim) / height));
            height = effectiveMaxDim;
          }
        }

        const renderAt = (w: number, h: number, q: number): string | null => {
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) return null;
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);

          // Try modern WebP first (supports high clarity at 1/3 the file size of JPEG)
          try {
            const webpData = canvas.toDataURL("image/webp", q);
            if (webpData.startsWith("data:image/webp")) {
              return webpData;
            }
          } catch {
            // Fallback to JPEG if browser doesn't support WebP canvas export
          }
          return canvas.toDataURL("image/jpeg", q);
        };

        let result = renderAt(width, height, effectiveQuality);
        if (!result) {
          resolve(String(e.target?.result));
          return;
        }

        // Gentle quality adjustment if extraordinarily complex photo exceeds budget
        let curW = width;
        let curH = height;
        let curQ = effectiveQuality;
        let attempts = 0;
        while (result.length > MAX_BASE64_LENGTH && attempts < 2) {
          attempts++;
          curQ = Math.max(0.72, curQ - 0.06);
          // Never drop below 850px so expanded images remain razor-sharp and text legible
          curW = Math.max(850, Math.round(curW * 0.92));
          curH = Math.max(850, Math.round(curH * 0.92));
          const next = renderAt(curW, curH, curQ);
          if (next && next.length < result.length) {
            result = next;
          } else {
            break;
          }
        }

        resolve(result);
      };
      img.onerror = () => {
        resolve(String(e.target?.result));
      };
      img.src = String(e.target?.result);
    };
    reader.onerror = () => reject(new Error(`Failed to read file ${file.name}`));
    reader.readAsDataURL(file);
  });
}

export function MultiImageUpload({
  value = [],
  onChange,
  maxPhotos = 8,
  label = "Product Photos",
  description = "The first photo is the primary cover image displayed across catalogs.",
  className,
  disabled = false,
}: MultiImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (fileList: FileList | File[]) => {
    if (disabled || isUploading) return;
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;

    const remainingSlots = Math.max(0, maxPhotos - value.length);
    if (remainingSlots <= 0) return;

    const filesToUpload = files.slice(0, remainingSlots);
    setIsUploading(true);

    try {
      // Process sequentially to strictly preserve user's chosen file order
      const newUrls: string[] = [];
      for (const file of filesToUpload) {
        const dataUrl = await compressAndReadImage(file);
        if (dataUrl) {
          newUrls.push(dataUrl);
        }
      }
      onChange([...value, ...newUrls]);
    } catch (err) {
      console.error("Failed to load image files", err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled || isUploading) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removePhoto = (index: number) => {
    if (disabled) return;
    const next = value.filter((_, i) => i !== index);
    onChange(next);
  };

  const makeCover = (index: number) => {
    if (disabled || index === 0 || index >= value.length) return;
    const selected = value[index];
    const rest = value.filter((_, i) => i !== index);
    onChange([selected, ...rest]);
  };

  const moveEarlier = (index: number) => {
    if (disabled || index <= 0) return;
    const next = [...value];
    const temp = next[index - 1];
    next[index - 1] = next[index];
    next[index] = temp;
    onChange(next);
  };

  const moveLater = (index: number) => {
    if (disabled || index >= value.length - 1) return;
    const next = [...value];
    const temp = next[index + 1];
    next[index + 1] = next[index];
    next[index] = temp;
    onChange(next);
  };

  const canAddMore = value.length < maxPhotos && !disabled;

  return (
    <div className={cn("space-y-2.5", className)}>
      <div className="flex flex-wrap items-center justify-between gap-1">
        <div>
          <label className="text-sm font-semibold text-foreground flex items-center gap-1.5">
            <ImageIcon className="h-4 w-4 text-primary" />
            {label}
            <span className="text-xs font-normal text-muted-foreground">
              ({value.length}/{maxPhotos})
            </span>
          </label>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          )}
        </div>
        {value.length > 1 && (
          <span className="text-[11px] text-muted-foreground italic">
            Use arrows or ⭐ to reorder
          </span>
        )}
      </div>

      {/* Hidden Multi-file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileChange}
        disabled={disabled || !canAddMore}
      />

      {/* When no images exist: Large dropzone */}
      {value.length === 0 ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragOver(true);
          }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          className={cn(
            "border-2 border-dashed rounded-xl p-6 sm:p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-all duration-200 text-center",
            isDragOver
              ? "border-primary bg-primary/10 scale-[0.99]"
              : "border-border hover:border-primary/50 hover:bg-muted/40",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            {isUploading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <UploadCloud className="h-6 w-6" />
            )}
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {isUploading ? "Processing photos..." : "Click or drag photos here to upload"}
            </p>
            <p className="text-xs text-muted-foreground">
              Select multiple photos at once. JPG, PNG, WEBP up to 10MB each (max {maxPhotos}).
            </p>
          </div>
        </div>
      ) : (
        /* Thumbnail Gallery Grid */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {value.map((url, index) => {
            const isCover = index === 0;
            return (
              <div
                key={`${url.slice(0, 32)}-${index}`}
                className={cn(
                  "relative aspect-square rounded-xl overflow-hidden border bg-muted/30 group shadow-xs transition-all",
                  isCover ? "border-primary ring-2 ring-primary/20" : "border-border"
                )}
              >
                {/* Photo Image */}
                <img
                  src={url}
                  alt={`Product view ${index + 1}`}
                  className="w-full h-full object-cover"
                />

                {/* Badge: Cover / Order */}
                <div className="absolute top-2 left-2 z-10">
                  {isCover ? (
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary text-primary-foreground text-[10px] font-bold tracking-wide shadow-sm uppercase">
                      <Star className="h-3 w-3 fill-current" /> Cover
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 rounded-md bg-black/60 backdrop-blur-xs text-white text-[10px] font-medium shadow-sm">
                      #{index + 1}
                    </span>
                  )}
                </div>

                {/* Delete button (always accessible) */}
                <button
                  type="button"
                  onClick={() => removePhoto(index)}
                  disabled={disabled}
                  className="absolute top-2 right-2 z-10 p-1.5 rounded-md bg-black/60 hover:bg-destructive text-white transition-colors shadow-sm"
                  title="Remove photo"
                  aria-label="Remove photo"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>

                {/* Reordering toolbar at bottom of tile */}
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-1.5 flex items-center justify-between text-white transition-opacity">
                  <div className="flex items-center gap-1">
                    {/* Move Left */}
                    <button
                      type="button"
                      onClick={() => moveEarlier(index)}
                      disabled={disabled || index === 0}
                      className={cn(
                        "p-1 rounded hover:bg-white/20 transition-colors",
                        index === 0 && "opacity-30 cursor-not-allowed"
                      )}
                      title="Move left in order"
                      aria-label="Move earlier"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </button>

                    {/* Move Right */}
                    <button
                      type="button"
                      onClick={() => moveLater(index)}
                      disabled={disabled || index === value.length - 1}
                      className={cn(
                        "p-1 rounded hover:bg-white/20 transition-colors",
                        index === value.length - 1 && "opacity-30 cursor-not-allowed"
                      )}
                      title="Move right in order"
                      aria-label="Move later"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  {/* Make Cover Button if not cover */}
                  {!isCover && (
                    <button
                      type="button"
                      onClick={() => makeCover(index)}
                      disabled={disabled}
                      className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-white/20 hover:bg-primary text-white font-medium transition-colors"
                      title="Make this the primary cover photo"
                    >
                      <Star className="h-2.5 w-2.5" /> Set Cover
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {/* "+ Add More" Drop/Click Card */}
          {canAddMore && (
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
              className={cn(
                "aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all duration-200 text-center p-3 bg-muted/20",
                isDragOver
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50 hover:bg-muted/40"
              )}
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-6 w-6 text-primary animate-spin" />
                  <span className="text-xs text-muted-foreground font-medium">Adding...</span>
                </>
              ) : (
                <>
                  <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Plus className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-semibold text-foreground">Add Photo</span>
                  <span className="text-[10px] text-muted-foreground">Select or drop</span>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
